import { Box, Typography, Stack } from "@mui/material";
import { FC, useState, useMemo, useEffect, useRef } from "react";
import { useTheme } from "@mui/material/styles";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";

interface SmallDonutChartProps {
  data: {
    id: string;
    label: string;
    value: number;
    color: string;
    displayText?: string;
  }[];
  onClick?: (label: string) => void;
  suffix?: string;
}

export const SmallDonutChart: FC<SmallDonutChartProps> = ({
  data,
  onClick,
  suffix = "",
}) => {
  const theme = useTheme();
  const [activeSlice, setActiveSlice] = useState<string | null>(null);
  const [clickedSlice, setClickedSlice] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const chartContainerRef = useRef<HTMLDivElement>(null);

  // mobile detection
  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth <= 768 || "ontouchstart" in window);
    };

    checkIsMobile();
    window.addEventListener("resize", checkIsMobile);

    return () => window.removeEventListener("resize", checkIsMobile);
  }, []);

  // handle clicks outside the chart on mobile
  useEffect(() => {
    if (!isMobile || !clickedSlice) return;

    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (
        chartContainerRef.current &&
        !chartContainerRef.current.contains(event.target as Node)
      ) {
        setClickedSlice(null);
        setActiveSlice(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [isMobile, clickedSlice]);

  // calculate total and percentages (hooks must be before any return)
  const total = useMemo(
    () =>
      data && data.length > 0
        ? data.reduce((sum, item) => sum + item.value, 0)
        : 0,
    [data],
  );

  const dataWithPercentage = useMemo(
    () =>
      data && data.length > 0
        ? data.map((item) => ({
            ...item,
            percentage: ((item.value / total) * 100).toFixed(1),
            displayText: item.displayText,
          }))
        : [],
    [data, total],
  );

  // determine which slice to show in the center
  const displayedSlice = useMemo(() => {
    if (isMobile && clickedSlice) {
      return clickedSlice;
    }
    return activeSlice;
  }, [isMobile, clickedSlice, activeSlice]);

  // create Highcharts options
  const chartOptions = useMemo(() => {
    const activeData = displayedSlice
      ? dataWithPercentage.find((item) => item.id === displayedSlice)
      : null;

    return {
      chart: {
        type: "pie",
        height: 150,
        backgroundColor: "transparent",
        margin: [10, 10, 10, 10],
        spacing: [0, 0, 0, 0],
      },
      title: {
        text: activeData
          ? `<div style="text-align: center;">
               <div style="font-size: 14px; font-weight: 600; color: ${theme.palette.text.primary}; margin-bottom: 2px;">
                 ${activeData.displayText || `${activeData.percentage}%`}
               </div>
               <div style="font-size: 12px; color: ${theme.palette.text.secondary};">
                 ${activeData.label}
               </div>
             </div>`
          : "",
        align: "center",
        verticalAlign: "middle",
        useHTML: true,
        style: {
          fontSize: "14px",
          fontWeight: "600",
        },
      },
      plotOptions: {
        pie: {
          innerSize: "60%",
          dataLabels: {
            enabled: false,
          },
          enableMouseTracking: true,
          states: {
            hover: {
              halo: {
                size: 4,
              },
            },
          },
          borderWidth: 0,
          point: {
            events: {
              mouseOver: function (this: any) {
                if (!isMobile) {
                  setActiveSlice(this.name);
                }
              },
              mouseOut: function () {
                if (!isMobile) {
                  setActiveSlice(null);
                }
              },
              click: function (this: any) {
                if (isMobile) {
                  // on mobile, the click maintains the state
                  if (clickedSlice === this.name) {
                    setClickedSlice(null);
                    setActiveSlice(null);
                  } else {
                    setClickedSlice(this.name);
                    setActiveSlice(this.name);
                  }
                } else {
                  // on desktop, normal behavior
                  setActiveSlice(activeSlice === this.name ? null : this.name);
                }
                if (
                  onClick &&
                  this.options &&
                  typeof this.options.label === "string"
                ) {
                  onClick(this.options.label);
                }
              },
            },
          },
        },
      },
      series: [
        {
          name: "Data",
          data: dataWithPercentage.map((item) => ({
            name: item.id,
            y: item.value,
            color: item.color,
            label: item.label,
            percentage: item.percentage,
            displayText: item.displayText,
          })),
        },
      ],
      credits: {
        enabled: false,
      },
      legend: {
        enabled: false,
      },
      tooltip: {
        enabled: false,
      },
    };
  }, [
    dataWithPercentage,
    displayedSlice,
    theme,
    isMobile,
    activeSlice,
    clickedSlice,
    onClick,
  ]);

  // Data validation
  if (!data || data.length === 0) {
    return (
      <Box sx={{ textAlign: "center", py: 4 }}>
        <Typography variant="body2" color="text.secondary">
          No data available
        </Typography>
      </Box>
    );
  }

  return (
    <Box ref={chartContainerRef}>
      {/* Donut Chart */}
      <Box sx={{ height: 150, width: "100%", mb: 2 }}>
        <HighchartsReact
          containerProps={{ style: { height: "100%", width: "100%" } }}
          highcharts={Highcharts}
          options={chartOptions}
        />
      </Box>

      {/* Legend */}
      <Stack spacing={0.5}>
        {dataWithPercentage.map((item) => (
          <Box
            key={item.id}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              cursor: "pointer",
              p: 0.5,
              borderRadius: 1,
              backgroundColor:
                displayedSlice === item.id ? "action.hover" : "transparent",
              transition: "background-color 0.2s",
              "&:hover": {
                backgroundColor: "action.hover",
              },
            }}
            onMouseEnter={() => {
              if (!isMobile) {
                setActiveSlice(item.id);
              }
            }}
            onMouseLeave={() => {
              if (!isMobile) {
                setActiveSlice(null);
              }
            }}
            onClick={() => {
              if (isMobile) {
                // on mobile, the click maintains the state
                if (clickedSlice === item.id) {
                  setClickedSlice(null);
                  setActiveSlice(null);
                } else {
                  setClickedSlice(item.id);
                  setActiveSlice(item.id);
                }
              } else {
                // on desktop, normal behavior
                setActiveSlice(activeSlice === item.id ? null : item.id);
              }
              if (onClick) {
                onClick(item.label);
              }
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  backgroundColor: item.color,
                  flexShrink: 0,
                }}
              />
              <Typography variant="body2" color="text.secondary">
                {item.label}
              </Typography>
            </Box>
            <Box sx={{ textAlign: "right" }}>
              <Typography variant="body2" fontWeight={600}>
                {item.value} {suffix}
              </Typography>
            </Box>
          </Box>
        ))}
      </Stack>
    </Box>
  );
};
